import React, { useState, useEffect, useRef } from 'react';
import { oneOfType, number, string } from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

// global
import Layout from '../../../Layout/StandardLayout';
import SecondaryPanel from '../../../Layout/SecondaryPanel';
import { setLoadingStatus, setSavingStatus } from '../../../../reducers/notification';

// local
import { load as loadBluesnap, init as initBluesnap } from './bluesnap';
import { updateData, getPostValues, updateDataBeforeSubmission, updateDataAfterSubmission, showErrors } from './utils';
import Wizard from './WizardMulti';
import { BusinessDetails, Owners, GenInformation, BusinessAndOwnership } from './StepsMulti';
import BluesnapFields from './BluesnapFields';
import { getBluesnapToken, addBankAccount, addSolaBankAccount, getBankAccountFormData } from '../apiRequests';
import usePaymentProvider from '../usePaymentProvider';
import { notificationError } from '../../../../core/actions';

/**
 * @param data      wizard form state
 * @param forSola   include the fields only Sola's boarding application needs.
 *
 * The Sola fields are gated because BlueSnap posts this same object to /v1/merchant/:id.
 * Sending it full routing and account numbers would put credentials our servers never
 * previously received into the BlueSnap request -- that flow only ever sent the last four,
 * with its SDK capturing the rest directly.
 */
const getBusinessDetails = (data, forSola = false) => {
  const retrieve = field => data[field]?.value;
  const retrieveAddress = prefix => ({
    line1: retrieve(`${prefix}Address`),
    city: retrieve(`${prefix}City`),
    state: retrieve(`${prefix}State`),
    zip: retrieve(`${prefix}Zip`),
  });
  const retrievePerson = prefix => ({
    firstName: retrieve(`${prefix}FirstName`),
    lastName: retrieve(`${prefix}LastName`),
    dateOfBirth: retrieve(`${prefix}DateOfBirth`),
    tel: retrieve(`${prefix}Phone`),
    percent: retrieve(`${prefix}Percent`),
    address: retrieveAddress(prefix),
  });

  // Sola requires the full 9-digit SSN per signer, plus a title. The BlueSnap field holds
  // only the last four, and BlueSnap needs neither.
  const retrieveSolaPerson = prefix => ({
    ...retrievePerson(prefix),
    ssn: retrieve(`${prefix}SSN`),
    title: 'Owner',
  });

  const buildPerson = forSola ? retrieveSolaPerson : retrievePerson;

  // In this wizard the company representative is derived from owner 1 (see the prefill
  // block below), and there is no separate companyRepSSN input -- so the representative's
  // SSN comes from owner 1's.
  const buildRepresentative = () => {
    const rep = buildPerson('companyRep');

    if (forSola && !rep.ssn) {
      return { ...rep, ssn: retrieve('owner1SSN') };
    }

    return rep;
  };
  const solaOnly = {};

  if (forSola) {
    // Collected by the wizard already, but never sent under BlueSnap -- its SDK read these
    // from the DOM. Sola's application needs them in the request.
    solaOnly.taxId = retrieve('businessTaxId');
    // BlueSnap sent a hardcoded support@clientbook.com through a hidden SDK input, so the
    // wizard never asked. Sola needs the MERCHANT's own address.
    solaOnly.email = retrieve('businessEmail');
    solaOnly.businessStartDate = retrieve('businessStartDate');
    solaOnly.monthlySalesVolume = retrieve('businessMonthlySalesVolume');
    solaOnly.banking = {
      bankName: retrieve('bankName'),
      routingNumber: retrieve('bankRoutingNumber'),
      accountNumber: retrieve('bankAccountNumber'),
    };
  }

  return {
    ...solaOnly,
    nickname: retrieve('bankAccountNickname'),
    dba: retrieve('businessName'),
    name: retrieve('businessLegalName'),
    description: retrieve('businessProductAndServiceDesc'),
    tel: retrieve('businessPhone'),
    website: retrieve('businessWebsite'),
    type: retrieve('businessType'),
    averageTransactionAmount: retrieve('businessAverageTransactionAmount'),
    highestTransactionAmount: retrieve('businessHighestTransactionAmount'),
    riskMonitoring: retrieve('businessRiskMonitoring'),
    riskPayment: retrieve('businessRiskPayment'),
    riskByCommission: retrieve('businessRiskByCommission'),
    address: retrieveAddress('business'),
    representative: buildRepresentative(),
    owners: ['owner1', 'owner2', 'owner3', 'owner4'].map(buildPerson).filter(o => o.firstName !== undefined),
    stores: retrieve('stores'),
  };
};

const setupBluesnapSuccess = (id, setData, history, onError, retrieveData) => pfToken => {
  return addBankAccount(id, {
    ...getPostValues(),
    pfToken,
    businessDetails: getBusinessDetails(retrieveData()),
  })
    .then(() => {
      updateDataAfterSubmission(setData);
      setSavingStatus(false);
      history.push('/BankAccount/list');
    })
    .catch(errors => {
      updateDataAfterSubmission(setData);
      onError(errors, setData);
      setSavingStatus(false);
    });
};

/**
 * Sola submission. There is no SDK to hand this to, so the collected form goes straight to
 * our API in one request -- including the store assignments this wizard collects.
 */
const submitSolaApplication = (id, setData, history, onError, retrieveData) => {
  return addSolaBankAccount(id, {
    ...getPostValues(),
    businessDetails: getBusinessDetails(retrieveData(), true),
  })
    .then(() => {
      updateDataAfterSubmission(setData);
      setSavingStatus(false);
      history.push('/BankAccount/list');
    })
    .catch(errors => {
      updateDataAfterSubmission(setData);
      onError(errors, setData);
      setSavingStatus(false);
    });
};

const setupBluesnapError =
  (setData, onError) =>
  (errors = []) => {
    updateDataAfterSubmission(setData);
    onError(errors, setData);
    setSavingStatus(false);
    notificationError('Bank account was not added');
  };

const AddAccount = ({ merchantId, bankAccountList }) => {
  // null until the payments backend answers; nothing loads or renders before then.
  const provider = usePaymentProvider(merchantId);
  const isSola = provider === 'SOLA';
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState(null);
  const history = useHistory();
  // used for bluesnap errors (both FE & BE)
  const [errors, setErrors] = useState([]);

  const [addBankAccountCrumb, setAddBankAccountCrumb] = useState({
    stores: {
      crumb: 'General Information',
      title: 'Stores',
      Component: GenInformation,
    },
    bizAndOwnership: {
      crumb: 'Business and Ownership Details',
      title: 'Business Details',
      Component: BusinessAndOwnership,
    },
  });

  // changing steps/pages
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const finalStepIndex = Object.keys(addBankAccountCrumb).length - 1;

  const previous = () => {
    setActiveStepIndex(Math.max(activeStepIndex - 1, 0));
  };
  const next = () => {
    setActiveStepIndex(Math.min(activeStepIndex + 1, finalStepIndex));
  };

  const [disableContinue, setDisableContinue] = useState(false);
  const wizardForm = useRef(null);

  const dataRef = useRef();
  dataRef.current = data;

  // get fields
  const getFields = () => {
    const fields = Array.from(wizardForm.current.elements)
      .filter(({ id }) => id)
      .map(el => {
        // The visible label, for error messages that read as English rather than as ids. Read
        // from the input's own Field wrapper: the summary step renders read-only twins with the
        // SAME ids earlier in the DOM, so htmlFor (and el.labels) resolve to the twin instead.
        const ownLabel = el.closest('.field')?.querySelector('label');
        const label = ownLabel || (el.labels && el.labels[0]);

        return {
          id: el.id,
          bluesnap: el.id,
          label: label ? label.textContent.trim() : el.id,
          value: el.value,
          active: el.getAttribute('data-active') === 'true',
          optional: el.getAttribute('data-optional') === 'true',
          forget: el.getAttribute('data-forget') === 'true',
          minLength: el.getAttribute('minlength'),
          maxLength: el.getAttribute('maxlength'),
          inputMode: el.getAttribute('inputmode'),
          type: el.getAttribute('type'),
          // 'date' for MM/DD/YYYY inputs (see FIELD_PROPS.DATE).
          format: el.getAttribute('data-format'),
          errorMessage: el.getAttribute('data-error-message'),
        };
      });

    return fields;
  };

  // helper to get values from fields
  const getValues = (fields, isSaving) =>
    fields.reduce((obj, { id, value, forget }) => {
      return {
        ...obj,
        [id]: isSaving && forget ? null : value,
      };
    }, {});

  // handler to update values by saving in the parent component
  // use instead of setData for changes that cause re-render
  const updateValues = (optionalValues = {}) => {
    const allFields = getFields();
    const allFieldsValues = getValues(allFields);

    updateData({ ...allFieldsValues, ...optionalValues }, setData);
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    document.getElementById('scrollWatchId').scrollTo(0, 0);
  };

  // common actions between next/prev step changes
  const handleStepChange = () => {
    // save values so we don't lose them on re-render
    updateValues();
    // reset the page
    setErrors([]);
    setDisableContinue(false);
    scrollToTop();
  };

  const handleErrors = (errs, set) => {
    if (errs && errs.length) {
      setErrors(list => [...list, ...errs]);
      showErrors(errs, set);
      setShowAll(true);
    }
  };

  useEffect(() => {
    getBankAccountFormData(merchantId);
  }, []);

  useEffect(() => {
    if (bankAccountList.formList) {
      let fakeObj = {};
      Object.keys(bankAccountList.formList).forEach(key => {
        fakeObj = {
          ...fakeObj,
          [key]: {
            hasError: false,
            value: bankAccountList.formList[key],
          },
        };
      });
      fakeObj = {
        ...fakeObj,
        owner1Address: fakeObj.businessAddress ? fakeObj.businessAddress : { hasError: false, value: '' },
        companyRepAddress: fakeObj.businessAddress ? fakeObj.businessAddress : { hasError: false, value: '' },
        companyRepCity: fakeObj.businessCity ? fakeObj.businessCity : { hasError: false, value: '' },
        companyRepCountry: { hasError: false, value: 'US' },
        companyRepDateOfBirth: fakeObj.owner1DateOfBirth ? fakeObj.owner1DateOfBirth : { hasError: false, value: '' },
        companyRepFirstName: fakeObj.owner1FirstName ? fakeObj.owner1FirstName : { hasError: false, value: '' },
        companyRepGovID: { hasError: false, value: '' },
        companyRepLastName: fakeObj.owner1LastName ? fakeObj.owner1LastName : { hasError: false, value: '' },
        companyRepPhone: fakeObj.businessPhone ? fakeObj.businessPhone : { hasError: false, value: '' },
        companyRepState: fakeObj.businessState ? fakeObj.businessState : { hasError: false, value: '' },
        companyRepZip: fakeObj.businessZip ? fakeObj.businessZip : { hasError: false, value: '' },
        stores: [],
      };
      setData(fakeObj);
    }
  }, [bankAccountList.formList]);

  useEffect(() => {
    if (!provider) {
      return;
    }

    setLoadingStatus(true);

    // Sola has no browser SDK for boarding, so neither the BlueSnap script nor its token is
    // fetched -- loading them would inject a third-party script and call a BlueSnap endpoint
    // for a merchant with no BlueSnap account.
    if (isSola) {
      updateData({}, setData);
      setLoadingStatus(false);
      return;
    }

    // get everything we need, async
    Promise.all([getBluesnapToken(merchantId), loadBluesnap()])
      .then(([token]) => {
        // set initial values
        updateData({}, setData);

        // init bluesnap
        const getData = () => dataRef.current;
        initBluesnap(
          token,
          () => getBluesnapToken(merchantId),
          setupBluesnapSuccess(merchantId, setData, history, handleErrors, getData),
          setupBluesnapError(setData, handleErrors),
        );
      })
      .catch(err => {
        console.log('### error loading BankAccount/Add', { err });
        notificationError('Page did not load properly. Please refresh.');
      })
      .finally(() => setLoadingStatus(false));
  }, [setData, merchantId, history, isSola, provider]);

  const handleSubmit = () => {
    setErrors([]);
    setSavingStatus(true);
    updateDataBeforeSubmission(setData);
    // Submission is NOT triggered here. updateDataBeforeSubmission appends a uuid to
    // businessName through a React state update, so reading dataRef.current now would give
    // pre-update values -- a dbaName without its uniquifying suffix, which Sola rejects as a
    // duplicate. BlueSnap submits from BluesnapFields' effect on isSubmitting; the effect
    // below does the same for Sola.
  };

  useEffect(() => {
    if (!isSola || !data?.isSubmitting) {
      return;
    }

    submitSolaApplication(merchantId, setData, history, handleErrors, () => dataRef.current);
    // handleErrors is deliberately not a dependency: it is redefined every render, so
    // depending on it would resubmit. The isSubmitting guard, plus updateDataAfterSubmission
    // clearing that flag, is what bounds this.
  }, [isSola, data?.isSubmitting, merchantId, history, setData]);

  const handleClick = (name, add) => {
    setShowAll(false);
    const theTempest = addBankAccountCrumb;
    if (name === 'business' && add) {
      if (add) {
        theTempest.business = {
          crumb: 'Business Details',
          title: 'Business Details',
          Component: BusinessDetails,
        };
        setActiveStepIndex(Math.min(activeStepIndex + 1, Object.keys(theTempest).length - 1));
      } else {
        delete theTemptest.business;
        previous();
      }
    }
    if (name === 'ownership' && add) {
      if (add) {
        theTempest.ownership = {
          crumb: 'Ownership',
          title: 'Ownership',
          Component: Owners,
        };
        setActiveStepIndex(Math.min(activeStepIndex + 1, Object.keys(theTempest).length - 1));
      } else {
        delete theTemptest.ownership;
        previous();
      }
    }

    if (!name && !add) {
      delete theTempest.business;
      delete theTempest.ownership;
      previous();
    }
    handleStepChange();
    setAddBankAccountCrumb(theTempest);
  };

  if (!provider) {
    return null;
  }

  return (
    <Layout title="Add Bank Account" classProps="standard-width-with-sidebar align-left">
      {data && (
        <Wizard
          activeStepIndex={activeStepIndex}
          setActiveStepIndex={setActiveStepIndex}
          finalStepIndex={finalStepIndex}
          previous={previous}
          next={next}
          data={data}
          errors={errors}
          setErrors={setErrors}
          setData={setData}
          onSubmit={handleSubmit}
          showAll={showAll}
          isSola={isSola}
          handleClick={handleClick}
          steps={addBankAccountCrumb}
          handleStepChange={handleStepChange}
          wizardForm={wizardForm}
          getFields={getFields}
          updateValues={updateValues}
          disableContinue={disableContinue}
          bankAccountStores={bankAccountList.formStores}
        />
      )}
      {/* BlueSnap's hidden capture fields exist only to be read by its SDK, which is not
          loaded on the Sola path. */}
      {data && !isSola && <BluesnapFields data={data} />}
    </Layout>
  );
};

AddAccount.propTypes = {
  merchantId: oneOfType([number, string]).isRequired,
};

AddAccount.defaultProps = {};

const mapStateToProps = state => ({
  merchantId: state.auth.merchantId,
  bankAccountList: state.bankAccounts,
});

export default connect(mapStateToProps)(AddAccount);
